// React.js
import { FC, FormEvent, useState } from 'react'
// Components
import { Button, Input } from '@components/ui'
// Helpers
import { inquireProduct } from './helpers'
import type { InquireProductFormInputs } from './helpers'
// Utils
import cn from 'clsx'
import { getAreaCodes } from '@utils/get-area-codes-phones'
// Styles
import s from './InquireOnlyProductForm.module.css'

interface Props {
  productSku: string
}

const InquireOnlyProductForm: FC<Props> = ({ productSku }) => {
  const [isLoading, setIsLoading] = useState(false)

  // Input values
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [areaCode, setAreaCode] = useState('+1')

  // Input messages

  const [firstNameInputError, setFirstNameInputError] = useState(false)
  const [lastNameInputError, setLastNameInputError] = useState(false)
  const [emailInputError, setEmailInputError] = useState(false)
  const [phoneNumberInputError, setPhoneNumberInputError] = useState(false)

  // Form global messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Area codes
  const areaCodes = getAreaCodes()

  const [inquireSucceded, setInquireSucceded] = useState(false)

  const handleSubmitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    // Check for blank inputs
    if (firstName.trim().length === 0) {
      setFirstNameInputError(true)
    } else if (lastName.trim().length === 0) {
      setLastNameInputError(true)
    } else if (email.trim().length === 0) {
      setEmailInputError(true)
    } else if (phoneNumber.trim().length === 0) {
      setPhoneNumberInputError(true)
    } else {
      const inputs: InquireProductFormInputs = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: `${areaCode} ${phoneNumber}`,
        productSku: productSku,
      }

      const { success, message } = await inquireProduct(inputs)

      if (success) {
        setSuccessMessage(message)
        setInquireSucceded(true)
      } else {
        setErrorMessage(message)
      }
    }
    setIsLoading(false)
  }

  const clearMessages = () => {
    setFirstNameInputError(false)
    setLastNameInputError(false)
    setEmailInputError(false)
    setPhoneNumberInputError(false)
    setSuccessMessage(null)
    setErrorMessage(null)
  }

  return (
    <form
      onSubmit={handleSubmitForm}
      className="w-80 xs:w-[420px] sm:w-[430px] lg:!w-full flex flex-col justify-between py-6 px-10"
    >
      <h3 className="font-heading text-2.5xl lg:text-3.5xl mb-5">
        Thank you for Your Interest!
      </h3>
      <span className="text-gray-dark text-sm lg:text-base mb-5">
        Please submit an inquiry below, and we’ll contact you within 24 hours to
        get additional information and answer any questions that you may have.
      </span>
      <div className="flex flex-col space-y-4">
        {successMessage && (
          <div className="text-green border border-green p-3">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="text-red border border-red p-3">{errorMessage}</div>
        )}
        {!inquireSucceded && (
          <>
            <div className={s.nameInputGroup}>
              <div className={s.nameInput}>
                <Input
                  type="text"
                  placeholder="First Name"
                  onChange={setFirstName}
                  onFocus={() => clearMessages()}
                  className={cn({ '!border-red': firstNameInputError })}
                />
                {firstNameInputError && (
                  <span className={s.inputErrorMessage}>
                    First Name Field is Required
                  </span>
                )}
              </div>
              <div className={s.nameInput}>
                <Input
                  type="text"
                  placeholder="Last Name"
                  onChange={setLastName}
                  onFocus={() => clearMessages()}
                  className={cn({ '!border-red': lastNameInputError })}
                />
                {lastNameInputError && (
                  <span className={s.inputErrorMessage}>
                    Last Name Field is Required
                  </span>
                )}
              </div>
            </div>
            <Input
              type="email"
              placeholder="Email Address"
              onChange={setEmail}
              onFocus={() => clearMessages()}
              className={cn({ '!border-red': emailInputError })}
            />
            {emailInputError && (
              <span className={s.inputErrorMessage}>
                Email Field is Required
              </span>
            )}
            <div className={s.phoneInputGroup}>
              <select
                name="area-codes"
                id="area-code-select"
                className={cn(s.select, {
                  '!border-red': phoneNumberInputError,
                })}
                onChange={(e) => setAreaCode(e.target.value)}
              >
                {areaCodes &&
                  areaCodes.length > 0 &&
                  areaCodes.map((areaCode) => (
                    <option key={areaCode} value={areaCode}>
                      {areaCode}
                    </option>
                  ))}
              </select>
              <Input
                type="tel"
                placeholder="Phone Number"
                onChange={setPhoneNumber}
                onFocus={() => clearMessages()}
                className={cn('!border-l !pl-4', {
                  '!border-red': phoneNumberInputError,
                })}
              />
            </div>
            {phoneNumberInputError && (
              <span className={s.inputErrorMessage}>
                Phone Number Field is Required
              </span>
            )}
            <Button
              variant="type1"
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className={s.button}
            >
              Submit
            </Button>
          </>
        )}
      </div>
    </form>
  )
}

export default InquireOnlyProductForm
